import React, { FC, useEffect, useState, useRef } from 'react';
import { LogoContainerProps } from './LogoContainer.types';
import { StyledLogoContainer } from './LogoContainer.styles';
import clsx from 'clsx';
import { getLogoName } from '../../utils/helper';

export const LogoContainer: FC<LogoContainerProps> = (props: LogoContainerProps) => {
  const { brand, aspectRatio, width, color, height, alignment = 'Centered', isLink, linkUrl, hoverColor } = props;
  const [svgContent, setSvgContent] = useState<string>('');
  const svgContainerRef = useRef<HTMLAnchorElement>(null);
  const svgAnchorRef = useRef<HTMLAnchorElement>(null);

  const logoName = getLogoName(brand);

  const logoUrl = `${process.env['NEXT_PUBLIC_ASSET_DOMAIN']}/mi-assets/mi-global/brand-logos/${logoName}.svg`;

  const fetchSvg = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  useEffect(() => {
    const fetchSvgContent = async () => {
      const content = await fetchSvg(logoUrl);
      if (content) {
        setSvgContent(content);
        updateSvgContent(content);
      } else {
        setSvgContent('');
      }
    };

    fetchSvgContent();
  }, [brand]);

  const updateSvgContent = (content: string) => {
    if (content) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'image/svg+xml');
      const svgElement = doc.querySelector('svg');

      const [scaleX, scaleY] = aspectRatio.split(':');

      if (svgElement !== null) {
        const wsf = Number(svgElement.getAttribute(`wsf-${scaleX}x${scaleY}`)).toFixed(2);
        const hsf = Number(svgElement.getAttribute(`hsf-${scaleX}x${scaleY}`)).toFixed(2);

        const scaledWidth = width * Number(wsf);
        const scaledHeight = height * Number(hsf);

        svgElement.style.width = `${scaledWidth}px`;
        svgElement.style.height = `${scaledHeight}px`;
        const stringifiedSVG: string = new XMLSerializer().serializeToString(svgElement);
        setSvgContent(stringifiedSVG);
      }
    } else {
      console.log('No SVG elements found.');
    }
  };

  if (svgContainerRef.current && svgContent) {
    svgContainerRef.current.innerHTML = svgContent;
    svgContainerRef.current.setAttribute('aria-label', `${logoName} Logo`);
  }

  return (
    <StyledLogoContainer
      data-testid="ui-library-LogoContainer"
      data-component-name="m-ui-library-LogoContainer"
      as={isLink ? 'a' : 'span'}
      href={linkUrl || undefined}
      data-logoid={brand}
      containerWidth={width}
      containerHeight={height}
      isLink={isLink || false}
      ref={svgAnchorRef}
      hoverColor={hoverColor || ''}
    >
      <span
        ref={svgContainerRef}
        className={clsx(
          { 'bottom-left': alignment === 'Bottom Left' },
          { 'center-left': alignment === 'Center Left' },
          { 'bottom-center': alignment === 'Bottom Centered' },
          { centered: alignment === 'Centered' },
          { 'color-logo': color === 'color' },
          { 'background-aware-logo': color === 'background-aware' }
        )}
      ></span>
    </StyledLogoContainer>
  );
};
